import { forEach } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledButtonDropdown } from 'reactstrap';
import { Tooltip } from '../Components/Tooltip';
import { bind } from '../Utils/Bind';
import { BsCaretDownFill } from '@react-icons/all-files/bs/BsCaretDownFill';
import { BsCaretUpFill } from '@react-icons/all-files/bs/BsCaretUpFill';
import { BsSlashCircle } from '@react-icons/all-files/bs/BsSlashCircle';
import { BsFileEarmark } from '@react-icons/all-files/bs/BsFileEarmark';
import { BsArrowDownShort } from '@react-icons/all-files/bs/BsArrowDownShort';
import { BsArrowUpShort } from '@react-icons/all-files/bs/BsArrowUpShort';

export enum SortOptions {
    Ascending = 'asc',
    Descending = 'desc',
    Unsort = 'unsort',
}

export interface ITableHeadOptions {
    onChange(value: string[]): void;
    onStickyChange?(): void;
    readonly value: readonly string[];
    readonly field?: string;
    readonly exclusive?: boolean;
    readonly button?: boolean;
    readonly className?: string;
    readonly label?: string;
    readonly showStickyHeaders?: boolean;
}

@observer
export class TableHeadOptions extends React.Component<ITableHeadOptions> {
    public render() {
        return (
            <>
                {!this.props.button && (
                    <UncontrolledButtonDropdown className="w-100">
                        <DropdownToggle nav className="p-0 w-100">
                            {this.props.children}
                            {this.hasMultiSort && (
                                <Tooltip message={`Sort Priority ${this.idx + 1}`}>{this.sortArrow}</Tooltip>
                            )}
                            {!this.hasMultiSort && this.sortArrow}
                        </DropdownToggle>
                        <DropdownMenu
                            end
                            className="p-0 dropdown-padding header-options-dropdown"
                            hidden={!this.props.field && !this.props.showStickyHeaders}
                        >
                            {this.props.field && (
                                <>
                                    <DropdownItem
                                        onClick={() => this.setSortStatus(SortOptions.Ascending)}
                                        className={this.buttonClass(SortOptions.Ascending)}
                                    >
                                        <BsArrowUpShort className="me-2" />
                                        Sort Ascending
                                    </DropdownItem>

                                    <DropdownItem
                                        onClick={() => this.setSortStatus(SortOptions.Descending)}
                                        className={this.buttonClass(SortOptions.Descending)}
                                    >
                                        <BsArrowDownShort className="me-2" />
                                        Sort Descending
                                    </DropdownItem>

                                    {this.showUnsort && (
                                        <DropdownItem
                                            onClick={() => this.setSortStatus(SortOptions.Unsort)}
                                            className={this.buttonClass() + ' text-danger'}
                                        >
                                            <BsSlashCircle className="me-2" /> Unsort
                                        </DropdownItem>
                                    )}
                                </>
                            )}

                            {this.props.onStickyChange && this.props.showStickyHeaders && (
                                <DropdownItem onClick={this.onStickyChange} className="fw-bold">
                                    <BsFileEarmark className="me-2" /> Toggle Sticky Column
                                </DropdownItem>
                            )}
                        </DropdownMenu>
                    </UncontrolledButtonDropdown>
                )}

                {this.props.button && (
                    <Tooltip message={this.tooltipMessage}>
                        <div onClick={this.onButtonClick} className={this.props.className + ' cursor-pointer'}>
                            {this.props.children}

                            <div className="d-inline-block ms-auto">{this.sortArrow}</div>
                        </div>
                    </Tooltip>
                )}
            </>
        );
    }

    @bind
    @action
    private onButtonClick() {
        const { value } = this.props;
        if (value.includes(this.props.field + ' ' + SortOptions.Ascending)) {
            this.setSortStatus(SortOptions.Descending);
        } else if (value.includes(this.props.field + ' ' + SortOptions.Descending)) {
            this.setSortStatus(SortOptions.Unsort);
        } else {
            this.setSortStatus(SortOptions.Ascending);
        }
    }

    @bind
    private buttonClass(order?: SortOptions) {
        const { value } = this.props;
        let className = 'text-header fw-bold py-0';

        if (value.includes(this.props.field + ' ' + order)) className += ' active';

        return className;
    }

    @bind
    @action
    private onStickyChange() {
        if (this.props.onStickyChange) this.props.onStickyChange();
    }

    @bind
    @action
    private setSortStatus(order: SortOptions) {
        const { field, exclusive } = this.props;
        let { value } = this.props;

        if (field) {
            this.sortStatus = order;

            if (exclusive) value = [];
            else value = value.filter(s => !s.startsWith(field));

            if (order && order != SortOptions.Unsort) {
                const fields = field.split(',');

                for (const val of fields) {
                    value = value.concat(val + ' ' + order);
                }
            }

            this.props.onChange(value.slice());
        }
    }

    @computed
    private get sortArrow() {
        const { value, field } = this.props;
        if (field) {
            const split = field.split(',');
            const sort = value.find(s => s.startsWith(split[0]));

            if (sort) {
                const order = sort.split(' ')[1];

                if (order == SortOptions.Ascending) {
                    return <BsCaretUpFill className="ms-1" />;
                } else if (order == SortOptions.Descending) {
                    return <BsCaretDownFill className="ms-1" />;
                }
            }
        }
    }

    @computed
    private get showUnsort() {
        const { value, field } = this.props;
        if (field) {
            const split = field.split(',');
            const sort = value.find(s => s.startsWith(split[0]));

            if (sort) {
                const order = sort.split(' ')[1];

                return !!order;
            }
        }

        return false;
    }

    @computed
    private get hasMultiSort() {
        const { value } = this.props;

        return value.length > 1 && this.idx != -1;
    }

    @computed
    private get idx() {
        const { value, field } = this.props;
        if (field) {
            return value.findIndex(s => s.startsWith(field));
        }

        return 0;
    }

    @computed
    private get tooltipMessage() {
        const name = this.props.label || this.props.field;
        switch (this.sortStatus) {
            case SortOptions.Ascending:
                return `Sort ${name} Descending`;
            case SortOptions.Descending:
                return `Unsort ${name}`;
            case SortOptions.Unsort:
                return `Sort ${name} Ascending`;
        }
    }

    @observable
    private sortStatus: SortOptions = SortOptions.Unsort;

    private readonly fontAwesomeClass = {
        asc: 'up',
        desc: 'down',
        unsort: 'sort',
    };
}
